<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="700px"
  >
    <template v-slot:activator="{ on: dialog }">
      <v-tooltip bottom>
        <template v-slot:activator="{ on: tooltip }">
          <v-btn
            color="blue"
            size="x-large"
            icon
            v-on="{ ...dialog, ...tooltip }"
          >
            <v-icon>
              mdi-magnify
            </v-icon>
          </v-btn>
        </template>
      
        <span>View details and documents</span>
      </v-tooltip>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      :loading="loading"
      color="hrissecondary"
      title="Claim Request Entry"
      text="View details and supporting documents"
      class="mb-0"
    >
      <template v-if="dialog && !loading">
        <v-container
          data-simplebar
        >
          <v-row dense>
            <v-col 
              v-if="claimRequestEntry.claim_request"
              cols="4"
            >
              Claimant:
            </v-col>
            <v-col 
              v-if="claimRequestEntry.claim_request"
              cols="1"
            >
              :
            </v-col>
            <v-col 
              v-if="claimRequestEntry.claim_request"
              cols="7"
            >
              <span class="font-weight-bold"> {{ claimRequestEntry.claim_request ? claimRequestEntry.claim_request.employee.person.name : '' }} </span>
            </v-col>

            <v-col cols="4">
              Claim Type:
            </v-col>
            <v-col cols="1">
              :
            </v-col>
            <v-col cols="7">
              <span class="font-weight-bold"> {{ claimRequestEntry.claim_type.name }} </span>
            </v-col>

            <template v-if="claimRequestEntry.receipt_date">
              <v-col cols="4">
                Receipt /Transaction Date
              </v-col>
              <v-col cols="1">
                :
              </v-col>
              <v-col cols="7">
                <span class="font-weight-bold"> {{ claimRequestEntry.receipt_date }} </span>
              </v-col>
            </template>

            <template v-if="claimRequestEntry.receipt_number">
              <v-col cols="4">
                Receipt Number
              </v-col>
              <v-col cols="1">
                :
              </v-col>
              <v-col cols="7">
                <span class="font-weight-bold"> {{ claimRequestEntry.receipt_number }} </span>
              </v-col>
            </template>

            <template v-if="dates.length > 0">
              <v-col cols="4">
                Claim Period
              </v-col>
              <v-col cols="1">
                :
              </v-col>
              <v-col cols="7">
                <div
                  v-for="item in dates"
                  :key="item.id"
                  class="font-weight-bold text-left"
                >
                  {{ $moment(item.date).format('YYYY-MM-DD') }} ({{ $moment(item.date).format('ddd') }}) 
                  {{ item.amount_to_deduct ? '- RM' + thousandSeparator((item.amount_to_deduct / 100).toFixed(2)) : '' }}
                </div>
              </v-col>
            </template>

            <template v-if="claimRequestEntry.claim_request_entry_employees.length > 0">
              <v-col cols="4">
                Combine Entitlement With
              </v-col>
              <v-col cols="1">
                :
              </v-col>
              <v-col cols="7">
                <div
                  v-for="item in claimRequestEntry.claim_request_entry_employees"
                  :key="item.id"
                >
                  <span class="font-weight-bold">{{ item.employee.person.name }}</span>
                </div>
              </v-col>
            </template>

            <template v-if="claimRequestEntry.entitlement_date_to_deduct">
              <v-col cols="4">
                Claim For
              </v-col>
              <v-col cols="1">
                :
              </v-col>
              <v-col cols="7">
                <span class="font-weight-bold"> {{ $moment(claimRequestEntry.entitlement_date_to_deduct).format('MMM YYYY') }} </span>
              </v-col>
            </template>

            <v-col cols="4">
              Claim Amount
            </v-col>
            <v-col cols="1">
              :
            </v-col>
            <v-col cols="7">
              <span class="font-weight-bold"> RM{{ thousandSeparator((claimRequestEntry.total_amount / 100).toFixed(2)) }} </span>
              
              <v-tooltip
                right
              >
                <template v-slot:activator="{ on }">
                  <v-icon
                    color="hrissecondary"
                    v-on="on"
                  >
                    mdi-information-outline
                  </v-icon>
                </template>
                RM{{ thousandSeparator((claimRequestEntry.amount / 100).toFixed(2)) }} x {{ claimRequestEntry.quantity }}
              </v-tooltip> 
            </v-col>

            <template v-if="claimRequestEntry.description">
              <v-col cols="4">
                Description
              </v-col>
              <v-col cols="1">
                :
              </v-col>
              <v-col cols="7">
                <span
                  class="font-weight-bold"
                  v-html="claimRequestEntry.description.replace(/\n/g, '</br>')"
                />
              </v-col>
            </template>
            
            <v-col
              cols="12"
              class="pa-0"
            >
              <v-row
                v-for="customField in claimRequestEntry.claim_request_custom_field_entries"
                :key="customField.id"
                class="pa-1"
                no-gutters
              >
                <v-col cols="4">
                  {{ customField.claim_type_custom_field.name }}
                </v-col>
                <v-col 
                  cols="1"
                  class="pl-1"
                >
                  :
                </v-col>
                <v-col 
                  cols="7"
                  class="pl-1"
                >
                  <span
                    class="font-weight-bold"
                  >
                    {{ customField.claim_type_custom_field.type === 'Selection' ? customField.claim_type_custom_field_selection.name : customField.value }}
                  </span>
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="12">
              <v-row>
                <v-col
                  v-for="document in claimRequestEntry.documents"
                  :key="document.id"
                  cols="12"
                  class="px-1 py-2"
                >
                  <v-chip
                    color="blue-grey lighten-3"
                    link
                    :href="document.file_url"
                    target="_blank"
                  >
                    <v-icon
                      color="red"
                      left
                    >
                      mdi-file-image
                    </v-icon>
                    <span class="text-truncate"> {{ document.file_name }} </span>
                  </v-chip>
                </v-col> 
              </v-row>
            </v-col>          

            <v-col cols="12">
              <v-row
                v-show="claimRequestEntry.claim_type.requires_declaration && claimRequestEntry.declaration_accepted"
                no-gutters
              >
                <div class="d-flex align-start">
                  <v-checkbox
                    v-model="claimRequestEntry.declaration_accepted"
                    readonly
                    color="green"
                    hide-details
                    class="mt-0 pa-0"
                  />
                  <span
                    v-html="claimRequestEntry.claim_type.declaration_text"
                  />
                </div>
              </v-row>
            </v-col>
          </v-row>
        </v-container>
      </template>
      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          @click="dialog = false"
        >
          Close
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  export default {
    props: {
      claimRequestEntry: {
        type: Object,
        required: true,
      },
    },
    data () {
      return {
        progressing: false,
        loading: false,
        dialog: null,
        dates: [],
      }
    },
    watch: {
      dialog (val) {
        if (val) {
          this.dates = this.claimRequestEntry.claim_request_entry_dates.sort((a, b) => new Date(a.date) - new Date(b.date))
        }
      },
    },
    methods: {
      thousandSeparator (amount) {
        if (amount !== '' || amount !== undefined || amount !== 0 || amount !== '0' || amount !== null) {
          return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        } else {
          return amount
        }
      },
    },
  }
</script>
